import {
  type RowboatDb,
  type RowboatDbOptions,
  StoreSupersededError,
} from '@jbroll/rowboat-client';
import { useEffect, useState } from 'react';

export type StoreWatch = Required<Pick<RowboatDbOptions, 'onBlocked' | 'onSuperseded'>>;

/**
 * The store `build` makes, once `hold` is granted when one is given, and whether its `ready()` has
 * resolved. Until then every synchronous read of it throws. `blocked` is true once its upgrade has
 * waited on another tab's connection. A failed open, and a store another tab later upgrades or
 * deletes, is thrown from render into the nearest error boundary.
 */
export function useOpenedStore(
  name: string,
  build: (name: string, watch: StoreWatch) => RowboatDb,
  hold?: (name: string) => Promise<void>,
): { db: RowboatDb | null; ready: boolean; blocked: boolean } {
  const [failure, setFailure] = useState<{ error: unknown } | null>(null);
  const [blocked, setBlocked] = useState(false);
  const [watch] = useState<StoreWatch>(() => ({
    onBlocked: () => setBlocked(true),
    onSuperseded: ({ reason }) => setFailure({ error: new StoreSupersededError(name, reason) }),
  }));
  const [db, setDb] = useState<RowboatDb | null>(() => (hold ? null : build(name, watch)));
  const [readyDb, setReadyDb] = useState<RowboatDb | null>(null);

  useEffect(() => {
    if (db !== null || !hold) return;
    let live = true;
    hold(name).then(
      () => {
        if (live) setDb(build(name, watch));
      },
      (error: unknown) => {
        if (live) setFailure({ error });
      },
    );
    return () => {
      live = false;
    };
  }, [db, name, build, hold, watch]);

  useEffect(() => {
    if (db === null) return;
    let live = true;
    db.ready().then(
      () => {
        if (live) setReadyDb(db);
      },
      (error: unknown) => {
        if (live) setFailure({ error });
      },
    );
    return () => {
      live = false;
    };
  }, [db]);

  if (failure) throw failure.error;
  return { db, ready: db !== null && readyDb === db, blocked };
}
